/** Type steps the value may take, largest first. Each is a theme text token. */
export const VALUE_STEPS = ['display-md', 'display-sm', 'display-xs'] as const;

export type ValueStep = (typeof VALUE_STEPS)[number];

export const VALUE_STEP_CLASS_NAMES: Record<ValueStep, string> = {
  'display-md': 'text-display-md',
  'display-sm': 'text-display-sm',
  'display-xs': 'text-display-xs',
};

/** Type classes every step shares, so a ruler measures exactly what the value draws. */
export const VALUE_CLASS_NAME = 'font-display text-primary whitespace-nowrap font-medium';

/** The row's `gap-4` between the value and the change. */
const VALUE_CHANGE_GAP_PX = 16;

/** Clearance a fit must leave, so a rounded width cannot flip a step. */
const FIT_SLACK_PX = 2;

/** Rendered widths, read synchronously from the DOM before paint. */
export interface IValueRowMetrics {
  rowWidth: number;
  changeWidth: number;
  /** Width of the value at each step, in `VALUE_STEPS` order. */
  valueWidths: readonly number[];
}

export interface IValueRowLayout {
  step: ValueStep;
  /** True when the value cannot sit beside the change at any step, so the change goes under it. */
  isWrapped: boolean;
}

function largestStepWithin(valueWidths: readonly number[], availableWidth: number): ValueStep | null {
  const index = valueWidths.findIndex((width) => width + FIT_SLACK_PX <= availableWidth);
  return index === -1 ? null : (VALUE_STEPS[index] ?? null);
}

/**
 * The largest step at which the value fits beside the change. When none does, the change wraps
 * under the value and the value takes the largest step that fits the row alone.
 *
 * Unmeasured or zero-width metrics resolve to the largest step, unwrapped.
 */
export function resolveValueRowLayout(metrics: IValueRowMetrics | null): IValueRowLayout {
  const largest = VALUE_STEPS[0];
  const smallest = VALUE_STEPS[VALUE_STEPS.length - 1] ?? largest;

  if (!metrics || metrics.rowWidth <= 0) {
    return { step: largest, isWrapped: false };
  }

  const beside = largestStepWithin(metrics.valueWidths, metrics.rowWidth - metrics.changeWidth - VALUE_CHANGE_GAP_PX);
  if (beside) {
    return { step: beside, isWrapped: false };
  }

  return { step: largestStepWithin(metrics.valueWidths, metrics.rowWidth) ?? smallest, isWrapped: true };
}

export function sameValueRowMetrics(a: IValueRowMetrics, b: IValueRowMetrics): boolean {
  return (
    a.rowWidth === b.rowWidth &&
    a.changeWidth === b.changeWidth &&
    a.valueWidths.length === b.valueWidths.length &&
    a.valueWidths.every((width, index) => width === b.valueWidths[index])
  );
}
